import React, { useState, useEffect } from 'react';
import { useTranslate } from 'react-admin';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Card,
  CardContent,
  Button,
  Typography,
  TextField,
  Switch,
  FormControlLabel,
  CircularProgress,
  Box,
  Grid,
  Paper,
  Step,
  Stepper,
  StepLabel,
  IconButton
} from '@mui/material';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { DashboardTemplateUI as DashboardTemplate, CreateFromTemplateRequest } from 'types/dashboard.ui';
import { dashboardAPI } from '../../api/dashboard';

interface DashboardCreationWizardProps {
  open: boolean;
  onClose: () => void;
  onSuccess: (dashboardId: string) => void;
}

interface FormData {
  name: string;
  description: string;
  is_public: boolean;
  selectedTemplate?: DashboardTemplate;
}

const DashboardCreationWizardMUI: React.FC<DashboardCreationWizardProps> = ({
  open,
  onClose,
  onSuccess,
}: DashboardCreationWizardProps) => {
  const translate = useTranslate();
  const [currentStep, setCurrentStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [templates, setTemplates] = useState<DashboardTemplate[]>([]);
  const [templatesLoading, setTemplatesLoading] = useState(false);
  const [formData, setFormData] = useState<FormData>({
    name: '',
    description: '',
    is_public: false,
    selectedTemplate: undefined,
  });

  const steps = [
    translate('resources.dashboards.wizard.step_template'),
    translate('resources.dashboards.wizard.step_settings'),
    translate('resources.dashboards.wizard.step_confirmation')
  ];

  useEffect(() => {
    if (open) {
      loadTemplates();
    }
  }, [open]);

  const loadTemplates = () => {
    setTemplatesLoading(true);
    dashboardAPI.getTemplates()
      .then((templatesData) => {
        // Убеждаемся, что получили массив
        setTemplates(Array.isArray(templatesData) ? templatesData : []);
      })
      .catch((error) => {
        console.error('Error loading templates:', error);
        // В случае ошибки устанавливаем пустой массив
        setTemplates([]);
      })
      .finally(() => {
        setTemplatesLoading(false);
      });
  };

  const handleNext = () => {
    if (currentStep < steps.length - 1) {
      setCurrentStep(currentStep + 1);
    }
  };

  const handlePrevious = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleSubmit = () => {
    setLoading(true);
    
    let dashboardPromise;
    if (formData.selectedTemplate) {
      const request: CreateFromTemplateRequest = {
        template_id: formData.selectedTemplate.id,
        name: formData.name,
        description: formData.description,
        is_public: formData.is_public,
      };
      dashboardPromise = dashboardAPI.createFromTemplate(request);
    } else {
      dashboardPromise = dashboardAPI.createDashboard({
        name: formData.name,
        description: formData.description,
        company_id: '',
        is_public: formData.is_public,
        layout_config: JSON.stringify({}),
      });
    }

    dashboardPromise
      .then((dashboard) => {
        onSuccess(dashboard.id);
      })
      .catch((error) => {
        console.error('Error creating dashboard:', error);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const handleTemplateSelect = (template: DashboardTemplate | undefined) => {
    setFormData(prev => ({
      ...prev,
      selectedTemplate: template,
      name: template?.name || '',
      description: template?.description || '',
    }));
  };

  const isStepValid = () => {
    switch (currentStep) {
      case 0:
        return true; // Всегда валидный, так как можно выбрать пустой дашборд
      case 1:
        return formData.name.trim() !== '';
      case 2:
        return true;
      default:
        return false;
    }
  };

  const renderStepContent = () => {
    switch (currentStep) {
      case 0:
        return (
          <Box>
            <Typography variant="h6" gutterBottom>
              {translate('resources.dashboards.wizard.select_template')}
            </Typography>
            <Typography variant="body2" color="textSecondary" sx={{ mb: 3 }}>
              {translate('resources.dashboards.wizard.select_template_hint')}
            </Typography>

            {templatesLoading ? (
              <Box display="flex" justifyContent="center" my={4}>
                <CircularProgress />
              </Box>
            ) : (
              <Grid container spacing={2}>
                {/* Опция пустого дашборда */}
                <Grid item xs={12} sm={6} md={4}>
                  <Paper
                    elevation={!formData.selectedTemplate ? 4 : 1}
                    sx={{
                      p: 2,
                      cursor: 'pointer',
                      border: !formData.selectedTemplate ? 2 : 1,
                      borderColor: !formData.selectedTemplate ? 'primary.main' : 'divider',
                      '&:hover': {
                        elevation: 3,
                      },
                    }}
                    onClick={() => handleTemplateSelect(undefined)}
                  >
                    {!formData.selectedTemplate && (
                      <Box display="flex" justifyContent="flex-end" mb={1}>
                        <CheckIcon color="primary" />
                      </Box>
                    )}
                    
                    <Typography variant="h6" gutterBottom>
                      {translate('resources.dashboards.wizard.empty_dashboard')}
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                      {translate('resources.dashboards.wizard.empty_dashboard_hint')}
                    </Typography>
                    
                    <Box mt={2}>
                      <Typography variant="caption" display="block">
                        {translate('resources.dashboards.wizard.widgets_count', { count: 0 })}
                      </Typography>
                      <Typography variant="caption" display="block">
                        {translate('resources.dashboards.wizard.category_empty')}
                      </Typography>
                    </Box>
                  </Paper>
                </Grid>
                
                {/* Существующие шаблоны */}
                {(templates || []).map((template) => (
                  <Grid item xs={12} sm={6} md={4} key={template.id}>
                    <Paper
                      elevation={formData.selectedTemplate?.id === template.id ? 4 : 1}
                      sx={{
                        p: 2,
                        cursor: 'pointer',
                        border: formData.selectedTemplate?.id === template.id 
                          ? 2 
                          : 1,
                        borderColor: formData.selectedTemplate?.id === template.id 
                          ? 'primary.main' 
                          : 'divider',
                        '&:hover': {
                          elevation: 3,
                        },
                      }}
                      onClick={() => handleTemplateSelect(template)}
                    >
                      {formData.selectedTemplate?.id === template.id && (
                        <Box display="flex" justifyContent="flex-end" mb={1}>
                          <CheckIcon color="primary" />
                        </Box>
                      )}
                      
                      <Typography variant="h6" gutterBottom>
                        {template.name}
                      </Typography>
                      <Typography variant="body2" color="textSecondary">
                        {template.description}
                      </Typography>
                      
                      <Box mt={2}>
                        <Typography variant="caption" display="block">
                          {translate('resources.dashboards.wizard.widgets_count', { count: template.widgets?.length || 0 })}
                        </Typography>
                        {template.category && (
                          <Typography variant="caption" display="block">
                            {translate('resources.dashboards.wizard.category_label', { category: template.category })}
                          </Typography>
                        )}
                      </Box>
                    </Paper>
                  </Grid>
                ))}
              </Grid>
            )}
          </Box>
        );

      case 1:
        return (
          <Box>
            <Typography variant="h6" gutterBottom>
              {translate('resources.dashboards.wizard.dashboard_settings')}
            </Typography>
            <Typography variant="body2" color="textSecondary" sx={{ mb: 3 }}>
              {translate('resources.dashboards.wizard.dashboard_settings_hint')}
            </Typography>

            <Box component="form" noValidate>
              <TextField
                fullWidth
                label={translate('resources.dashboards.wizard.dashboard_name')}
                value={formData.name}
                onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                margin="normal"
                required
                helperText={translate('resources.dashboards.wizard.dashboard_name_hint')}
              />

              <TextField
                fullWidth
                label={translate('resources.dashboards.wizard.dashboard_description')}
                value={formData.description}
                onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                margin="normal"
                multiline
                rows={3}
                helperText={translate('resources.dashboards.wizard.dashboard_description_hint')}
              />

              <FormControlLabel
                control={
                  <Switch
                    checked={formData.is_public}
                    onChange={(e) => setFormData(prev => ({ ...prev, is_public: e.target.checked }))}
                  />
                }
                label={translate('resources.dashboards.wizard.public_dashboard')}
                sx={{ mt: 2 }}
              />
              <Typography variant="caption" display="block" color="textSecondary">
                {translate('resources.dashboards.wizard.public_dashboard_hint')}
              </Typography>
            </Box>
          </Box>
        );

      case 2:
        return (
          <Box>
            <Typography variant="h6" gutterBottom>
              {translate('resources.dashboards.wizard.confirmation')}
            </Typography>
            <Typography variant="body2" color="textSecondary" sx={{ mb: 3 }}>
              {translate('resources.dashboards.wizard.confirmation_hint')}
            </Typography>

            <Card variant="outlined">
              <CardContent>
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <Typography variant="subtitle2" color="textSecondary">
                      {translate('resources.dashboards.wizard.template_label')}
                    </Typography>
                    <Typography variant="body1">
                      {formData.selectedTemplate?.name || translate('resources.dashboards.wizard.empty_dashboard')}
                    </Typography>
                  </Grid>
                  
                  <Grid item xs={12}>
                    <Typography variant="subtitle2" color="textSecondary">
                      {translate('resources.dashboards.wizard.name_label')}
                    </Typography>
                    <Typography variant="body1">
                      {formData.name}
                    </Typography>
                  </Grid>
                  
                  {formData.description && (
                    <Grid item xs={12}>
                      <Typography variant="subtitle2" color="textSecondary">
                        {translate('resources.dashboards.wizard.description_label')}
                      </Typography>
                      <Typography variant="body1">
                        {formData.description}
                      </Typography>
                    </Grid>
                  )}
                  
                  <Grid item xs={12}>
                    <Typography variant="subtitle2" color="textSecondary">
                      {translate('resources.dashboards.wizard.access_label')}
                    </Typography>
                    <Typography variant="body1">
                      {formData.is_public 
                        ? translate('resources.dashboards.values.public')
                        : translate('resources.dashboards.values.private')}
                    </Typography>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Box>
        );

      default:
        return null;
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: { minHeight: '500px' }
      }}
    >
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h6">
            {translate('resources.dashboards.wizard.title')}
          </Typography>
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent dividers>
        <Box sx={{ mb: 3 }}>
          <Stepper activeStep={currentStep} alternativeLabel>
            {steps.map((label) => (
              <Step key={label}>
                <StepLabel>{label}</StepLabel>
              </Step>
            ))}
          </Stepper>
        </Box>

        {renderStepContent()}
      </DialogContent>

      <DialogActions sx={{ justifyContent: 'space-between', p: 2 }}>
        <Button
          onClick={handlePrevious}
          disabled={currentStep === 0 || loading}
          startIcon={<ArrowBackIcon />}
        >
          {translate('resources.dashboards.wizard.back')}
        </Button>

        <Box>
          {currentStep < steps.length - 1 ? (
            <Button
              onClick={handleNext}
              disabled={!isStepValid() || loading}
              variant="contained"
              endIcon={<ArrowForwardIcon />}
            >
              {translate('resources.dashboards.wizard.next')}
            </Button>
          ) : (
            <Button
              onClick={handleSubmit}
              disabled={!isStepValid() || loading}
              variant="contained"
              startIcon={loading ? <CircularProgress size={20} /> : <CheckIcon />}
            >
              {loading 
                ? translate('resources.dashboards.wizard.creating') 
                : translate('resources.dashboards.wizard.create')}
            </Button>
          )}
        </Box>
      </DialogActions>
    </Dialog>
  );
};

export default DashboardCreationWizardMUI;